/**
 * DirectionProvider — Visual-language direction context.
 *
 * Wraps the visitor surface and exposes the tenant's chosen visual direction
 * (Atelier vs Concierge) to descendant components. Also writes the direction
 * to a `data-direction` attribute on <html>, which is what
 * `src/styles/directions.css` reads to swap the 24-token palette.
 *
 * The pattern is intentionally minimal — almost every visual divergence flows
 * through CSS variables, not JSX. Only the three named-slot components
 * (DirectionAvatar / DirectionGreeting / DirectionGcpCard) call useDirection()
 * to branch their render.
 *
 * Plan: docs/strategy/visitor-pwa-both-directions-plan-2026-05-16.md
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react';

export type Direction = 'atelier' | 'concierge';

/** Default = concierge (broader fit, sane fallback for offline / SSR). */
const DEFAULT_DIRECTION: Direction = 'concierge';

const DirectionContext = createContext<Direction>(DEFAULT_DIRECTION);

export interface DirectionProviderProps {
  /**
   * Tenant-supplied direction. When undefined (initial load / API error)
   * the provider falls back to DEFAULT_DIRECTION so the rest of the tree
   * has stable CSS-var values to read.
   */
  direction: Direction | undefined;
  children: ReactNode;
}

/**
 * Mount once at the top of the visitor surface (inside FeedbackPage, after
 * /api/status resolves). Setting data-direction on <html> rather than a
 * scoped element keeps all stylesheets — including third-party shadow
 * portals — in sync with the current theme.
 */
export function DirectionProvider({ direction, children }: DirectionProviderProps) {
  const resolved: Direction = direction ?? DEFAULT_DIRECTION;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.direction = resolved;
    return () => {
      // On unmount (e.g. navigation back to NotFoundPage) we leave the
      // attribute in place — the new route's tree will overwrite it via
      // its own provider, or fall back to CSS defaults if there is none.
    };
  }, [resolved]);

  return <DirectionContext.Provider value={resolved}>{children}</DirectionContext.Provider>;
}

/**
 * Read the current direction from anywhere inside <DirectionProvider/>.
 * Throws nothing — defaults to 'concierge' if used outside the provider,
 * which matches the schema default and lets unit tests render components
 * without setting up the provider.
 */
export function useDirection(): Direction {
  return useContext(DirectionContext);
}

/** True when the current visual language is Atelier (editorial / restraint). */
export function useIsAtelier(): boolean {
  return useDirection() === 'atelier';
}

/** True when the current visual language is Concierge (hospitality / named). */
export function useIsConcierge(): boolean {
  return useDirection() === 'concierge';
}
